import React from 'react';

// Base skeleton component with shimmer effect
export const Skeleton = ({ className = '', ...props }) => (
  <div 
    className={`skeleton bg-[#1a1a1a] ${className}`} 
    {...props}
  />
);

// Skeleton for stat cards on dashboard
export const StatCardSkeleton = () => (
  <div className="vault-card stat-card">
    <div className="flex items-center gap-3 mb-3">
      <Skeleton className="w-5 h-5 rounded" />
      <Skeleton className="h-3 w-20" />
    </div>
    <Skeleton className="h-8 w-24" />
  </div>
);

// Skeleton for moto card
export const MotoCardSkeleton = () => (
  <div className="flex items-center gap-4 p-4 bg-[#121212] border border-[#262626]">
    <Skeleton className="w-12 h-12 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <Skeleton className="h-5 w-32 mb-2" />
      <Skeleton className="h-4 w-20" />
    </div>
    <div className="text-right">
      <Skeleton className="h-6 w-16 mb-1" />
      <Skeleton className="h-3 w-8" />
    </div>
  </div>
);

// Skeleton for expense/spesa item
export const SpesaCardSkeleton = () => (
  <div className="bg-[#121212] border border-[#262626] p-4">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2">
          <Skeleton className="h-5 w-24 rounded" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-5 w-48 mb-2" />
        <Skeleton className="h-4 w-28" />
      </div>
      <div className="flex items-center gap-4">
        <Skeleton className="h-7 w-20" />
        <div className="flex gap-1">
          <Skeleton className="w-8 h-8" />
          <Skeleton className="w-8 h-8" />
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for maintenance item
export const ManutenzioneCardSkeleton = () => (
  <div className="bg-[#121212] border border-[#262626] p-4">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <Skeleton className="h-5 w-20 rounded" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-5 w-56 mb-2" />
        <div className="flex gap-4">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
      <Skeleton className="h-6 w-16" />
    </div>
  </div>
);

// Skeleton for trip/viaggio item
export const ViaggioCardSkeleton = () => (
  <div className="bg-[#121212] border border-[#262626] p-4">
    <div className="flex items-start gap-4">
      <Skeleton className="w-20 h-20 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <Skeleton className="h-6 w-40 mb-2" />
        <Skeleton className="h-4 w-48 mb-2" />
        <div className="flex gap-4">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for community post
export const CommunityPostSkeleton = () => (
  <div className="bg-[#121212] border border-[#262626] p-4">
    <div className="flex items-start gap-3 mb-3">
      <Skeleton className="w-10 h-10 rounded-full" />
      <div className="flex-1">
        <Skeleton className="h-5 w-32 mb-1" />
        <Skeleton className="h-3 w-24" />
      </div>
      <Skeleton className="h-5 w-16 rounded" />
    </div>
    <Skeleton className="h-6 w-3/4 mb-2" />
    <Skeleton className="h-4 w-full mb-1" />
    <Skeleton className="h-4 w-2/3 mb-4" />
    <div className="flex items-center gap-4">
      <Skeleton className="h-8 w-16" />
      <Skeleton className="h-8 w-20" />
    </div>
  </div>
);

// Skeleton for promemoria/reminder item
export const PromemoriaCardSkeleton = () => (
  <div className="flex items-center gap-4 p-3 bg-[#121212] border border-[#262626]">
    <Skeleton className="w-10 h-10 rounded" />
    <div className="flex-1 min-w-0">
      <Skeleton className="h-5 w-40 mb-1" />
      <Skeleton className="h-4 w-32" />
    </div>
    <div className="text-right">
      <Skeleton className="h-4 w-20 mb-1" />
      <Skeleton className="h-3 w-16" />
    </div>
  </div>
);

// Skeleton for equipment item
export const EquipaggiamentoCardSkeleton = () => (
  <div className="bg-[#121212] border border-[#262626] p-4">
    <div className="flex items-start gap-4">
      <Skeleton className="w-16 h-16 flex-shrink-0" />
      <div className="flex-1">
        <Skeleton className="h-5 w-36 mb-2" />
        <Skeleton className="h-4 w-24 mb-2" />
        <Skeleton className="h-4 w-20" />
      </div>
    </div>
  </div>
);

// Skeleton for document item
export const DocumentoCardSkeleton = () => (
  <div className="bg-[#121212] border border-[#262626] p-4">
    <div className="flex items-center gap-4">
      <Skeleton className="w-12 h-12 rounded" />
      <div className="flex-1">
        <Skeleton className="h-5 w-40 mb-2" />
        <Skeleton className="h-4 w-28" />
      </div>
      <Skeleton className="h-4 w-24" />
    </div>
  </div>
);

// Skeleton for fuel station item
export const FuelStationSkeleton = () => (
  <div className="p-3 bg-[#121212] border border-[#262626]">
    <div className="flex items-start gap-3">
      <Skeleton className="w-10 h-10 rounded" />
      <div className="flex-1">
        <Skeleton className="h-5 w-36 mb-1" />
        <Skeleton className="h-4 w-48 mb-2" />
        <div className="flex gap-2">
          <Skeleton className="h-5 w-16 rounded" />
          <Skeleton className="h-5 w-14 rounded" />
        </div>
      </div>
      <Skeleton className="h-5 w-12" />
    </div>
  </div>
);

// Grid skeleton for dashboard stats
export const DashboardStatsSkeleton = () => (
  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
    {[...Array(6)].map((_, i) => (
      <div key={i} className="list-item" style={{ animationDelay: `${i * 0.05}s` }}>
        <StatCardSkeleton />
      </div>
    ))}
  </div>
);

// List skeleton helper - renders multiple skeletons
export const ListSkeleton = ({ count = 5, Component }) => (
  <div className="space-y-3">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="list-item" style={{ animationDelay: `${i * 0.05}s` }}>
        <Component />
      </div>
    ))}
  </div>
);
